import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Group } from "../../../api/groupsApi";
import EmptyState from "../../../ui/feedback/EmptyState";
import FieldGroup from "../../../ui/forms/FieldGroup";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { groupPathName } from "./groupNames";

interface GroupChoiceFieldProps {
  legend: string;
  /** What the choice does, on hover or focus beside the legend. */
  hint?: string;
  /** Shown when there is nothing to choose. */
  none: string;
  groupIds: readonly string[];
  /** The groups that may be chosen, with the names the user can see. */
  groups: readonly Group[];
  /** Omitted: shown read-only. */
  onChange?: (groupIds: readonly string[]) => void;
  /** A choice before the groups that is no group of the list, such as the Administrators an
   * administrator's invitation joins. */
  children?: ReactNode;
}

/**
 * Choosing groups, the one way the web app does it: the groups new accounts join, and the
 * groups an invitation carries (GS-7). A chosen group that is gone shows as such, so it can be
 * removed.
 */
export default function GroupChoiceField({ legend, hint, none, groupIds, groups, onChange, children }: GroupChoiceFieldProps) {
  const { t } = useTranslation();
  const named = new Set(groupIds);
  const byId = new Map(groups.map((group) => [group.groupId, group]));
  const rows = [
    ...groups.map((group) => ({ id: group.groupId, name: groupPathName(group, groups) })),
    ...groupIds.filter((id) => !byId.has(id)).map((id) => ({ id, name: t("access.removedGroup") })),
  ];
  const setNamed = (id: string, on: boolean) => {
    const next = new Set(named);
    if (on) next.add(id);
    else next.delete(id);
    onChange?.([...next].sort());
  };

  return (
    <FieldGroup legend={legend} explanation={hint} className="space-y-1">
      {children}
      {rows.length === 0 ? (
        <EmptyState size="small">{none}</EmptyState>
      ) : (
        rows.map((row) => (
          <OptionLabel key={row.id}>
            <input type="checkbox" checked={named.has(row.id)} disabled={onChange === undefined}
              onChange={(event) => { setNamed(row.id, event.target.checked); }} />
            {row.name}
          </OptionLabel>
        ))
      )}
    </FieldGroup>
  );
}
